import { useState, useRef, useEffect } from 'react';
import { COLORS } from '../../constants/theme';

interface MultiSelectDropdownProps {
  options: string[];
  selected: string[];
  onChange: (selected: string[]) => void;
  placeholder?: string;
}

export function MultiSelectDropdown({ options, selected, onChange, placeholder = 'All' }: MultiSelectDropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const allSelected = selected.length === 0;
  const displayText = allSelected
    ? placeholder
    : selected.length === 1
      ? selected[0]
      : `${selected.length} selected`;

  function toggleOption(option: string) {
    if (allSelected) {
      // Switching from "All" → deselect this one option (select all others)
      onChange(options.filter(o => o !== option));
    } else if (selected.includes(option)) {
      const next = selected.filter(s => s !== option);
      onChange(next.length === 0 ? [] : next);
    } else {
      const next = [...selected, option];
      onChange(next.length === options.length ? [] : next);
    }
  }

  function toggleAll() {
    onChange([]);
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        style={{
          width: '100%',
          padding: '6px 8px',
          borderRadius: 4,
          backgroundColor: COLORS.inputBg,
          border: `1px solid ${isOpen ? COLORS.link : COLORS.inputBorder}`,
          color: allSelected ? COLORS.text.tertiary : COLORS.text.primary,
          fontSize: 13,
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {displayText}
        </span>
        <span
          style={{
            color: COLORS.text.muted,
            fontSize: 9,
            marginLeft: 6,
            transform: isOpen ? 'rotate(180deg)' : 'none',
            transition: 'transform 0.15s',
          }}
        >
          &#x25BC;
        </span>
      </button>
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            marginTop: 4,
            backgroundColor: COLORS.card,
            border: `1px solid ${COLORS.border}`,
            borderRadius: 6,
            zIndex: 100,
            maxHeight: 260,
            overflowY: 'auto',
            boxShadow: '0 8px 24px rgba(0,0,0,0.5)',
          }}
        >
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 8px',
              cursor: 'pointer',
              fontSize: '12px',
              color: allSelected ? COLORS.text.primary : COLORS.text.secondary,
              borderBottom: `1px solid ${COLORS.border}`,
            }}
          >
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
            />
            All
          </label>
          {options.map(option => (
            <label
              key={option}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                padding: '4px 8px',
                cursor: 'pointer',
                fontSize: '12px',
                color: (allSelected || selected.includes(option)) ? COLORS.text.primary : COLORS.text.secondary,
              }}
            >
              <input
                type="checkbox"
                checked={allSelected || selected.includes(option)}
                onChange={() => toggleOption(option)}
              />
              {option}
            </label>
          ))}
        </div>
      )}
    </div>
  );
}
